/** "What to do next" — the lead page's right column: AI suggestions, open
 *  work and its verbs, scheduling, link engagement, the album, notes. */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Calendar, Image, Mic, MousePointerClick, Sparkles, Square, StickyNote } from 'lucide-react';
import { CallWindow, Lead, LeadEngagement, Task } from '../../types';
import { actionApi, activityApi, leadApi, taskApi } from '../../services/api';
import { marketingApi, WhatsAppTemplate } from '../../services/marketingApi';
import { fmtDay, fmtTime, istDay, istToday, shiftDay } from '../../lib';
import { WhatsAppIcon, CallIcon } from '../BrandIcons';
import Sheet, { FoldCard, SheetLabel, errText } from './Sheet';
import { dueState, fmtDwell, isEngaged, taskBriefing, taskTitle } from './leadModel';

const TypeIcon: React.FC<{ type: string }> = ({ type }) =>
  type === 'call' ? <CallIcon className="w-4 h-4" />
    : type === 'whatsapp' ? <WhatsAppIcon className="w-4 h-4" />
      : type === 'site_visit' ? <span className="text-gold">📍</span>
        : <StickyNote className="w-4 h-4 text-graphite" strokeWidth={1.75} />;

/** Overdue: "37m" / "5h" / "Yesterday" / "Jun 24"; upcoming: time today,
 *  "Tomorrow", or the day. */
const timePill = (iso: string, now = new Date()): string => {
  const ms = now.getTime() - new Date(iso).getTime();
  const day = istDay(iso), today = istToday();
  if (ms > 0) {
    const m = Math.round(ms / 60000);
    if (m < 60) return `${m}m`;
    if (day === today) return `${Math.floor(m / 60)}h`;
    if (day === shiftDay(today, -1)) return 'Yesterday';
    return fmtDay(iso);
  }
  if (day === today) return fmtTime(iso);
  if (day === shiftDay(today, 1)) return 'Tomorrow';
  return fmtDay(iso);
};

const STATE_STYLE = {
  checking: { border: 'border-forest', pill: 'chip-forest' },
  late: { border: 'border-brick', pill: 'chip-brick' },
  grace: { border: 'border-gold', pill: 'chip-gold' },
  future: { border: 'border-charcoal/30', pill: 'chip-mute' },
};

/** IST wall-clock date + time → an honest instant. */
const istInstant = (date: string, time: string) => new Date(`${date}T${time}:00+05:30`).toISOString();
const istParts = (iso: string) => ({
  date: istDay(iso),
  time: new Intl.DateTimeFormat('en-GB', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', hour12: false }).format(new Date(iso)),
});

const SKIP_COPY: Record<string, string> = {
  already_suggested: 'Already suggested today. Check the Queue.',
  do_not_contact: 'This lead is marked do-not-contact.',
  lead_dropped: 'This lead has been dropped.',
  no_presales_assignment: 'No pre-sales advisor is assigned yet.',
  booked_visit_exists: 'A site visit is already booked.',
  covered_by_booking_flow: 'The visit booking already schedules the follow-ups.',
};

// ── AI suggestion ───────────────────────────────────────────────────────────

export const SuggestionCard: React.FC<{
  lead: Lead;
  proposed: Task[];
  onReview: (t: Task) => void;
  onChanged: () => void;
}> = ({ lead, proposed, onReview, onChanged }) => {
  const [thinking, setThinking] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  const suggest = async () => {
    setThinking(true); setNote(null);
    try {
      const r = await leadApi.suggest(lead.id);
      if (r.created && r.task) { onChanged(); onReview(r.task); }
      else if (r.skip_reason === 'no_action_warranted') setNote(r.suggestion?.reason || 'Nothing to suggest right now.');
      else if (r.skip_reason?.startsWith('pending_')) setNote(`There's already a pending ${r.skip_reason.slice(8).replace(/_exists$/, '').replace(/_/g, ' ')} for this lead.`);
      else setNote(SKIP_COPY[r.skip_reason || ''] || 'No suggestion this time.');
    } catch (e) { setNote(errText(e)); }
    setThinking(false);
  };

  return (
    <div className="card">
      <h3 className="card-h"><Sparkles className="w-4 h-4" strokeWidth={1.75} />AI suggestion</h3>
      {proposed.length > 0 ? (
        <div className="space-y-2">
          {proposed.map((t) => (
            <button key={t.id} type="button" onClick={() => onReview(t)}
              className="w-full text-left border-[1.5px] border-gold/50 bg-gold/5 rounded-lg px-3 py-2.5 hover:shadow-sketch">
              <span className="flex items-center gap-2">
                <TypeIcon type={t.type} />
                <b className="text-[13px] flex-1 min-w-0 truncate">{taskTitle(t).replace(/\s*\(AI suggested\)\s*/i, '') || 'Suggested follow-up'}</b>
                <span className="chip chip-mute">{timePill(t.due_at)}</span>
                <span className="chip chip-gold">✦ AI</span>
              </span>
              {t.payload?.suggestion?.reason && (
                <span className="block text-[12.5px] italic mt-1 line-clamp-2">“{t.payload.suggestion.reason}”</span>
              )}
              <span className="block text-[11.5px] text-graphite mt-1">Review to approve, adjust or reject</span>
            </button>
          ))}
        </div>
      ) : (
        <>
          <button className="btn btn-sm !border-gold text-amber-ink" disabled={thinking} onClick={suggest}>
            {thinking ? 'Thinking…' : '✦ Suggest next step'}
          </button>
          {note && <p className="text-[12.5px] text-graphite m-0 mt-2">{note}</p>}
        </>
      )}
    </div>
  );
};

// ── Upcoming ────────────────────────────────────────────────────────────────

export const UpcomingCard: React.FC<{
  tasks: Task[];
  windows: CallWindow[];
  onOpen: (t: Task) => void;
  onSchedule: () => void;
  onBookVisit: () => void;
}> = ({ tasks, windows, onOpen, onSchedule, onBookVisit }) => {
  const sorted = [...tasks].sort((a, b) => a.due_at.localeCompare(b.due_at));
  return (
    <div className="card">
      <h3 className="card-h"><Calendar className="w-4 h-4" strokeWidth={1.75} />Upcoming
        {sorted.length > 0 && <span className="ml-auto font-sans normal-case tracking-normal text-[11.5px] text-amber-ink">{sorted.length} open</span>}
      </h3>
      {sorted.length === 0 && <p className="text-graphite text-sm italic m-0">Nothing scheduled.</p>}
      <div className="space-y-2">
        {sorted.map((t) => {
          const st = dueState(t, windows);
          const brief = taskBriefing(t);
          return (
            <button key={t.id} type="button" onClick={() => onOpen(t)}
              className={`w-full text-left grid grid-cols-[20px_minmax(0,1fr)_auto] gap-2 items-start border-2 ${STATE_STYLE[st].border} rounded-lg px-2.5 py-2 bg-warm-white hover:shadow-sketch`}>
              <TypeIcon type={t.type} />
              <span className="min-w-0">
                <b className="block text-[13px] line-clamp-2">{taskTitle(t)}</b>
                {brief && <span className="block text-[12px] italic text-graphite line-clamp-3">“{brief}”</span>}
                {t.source === 'selldo' && <span className="block text-[11px] text-graphite">Created in Sell.do</span>}
              </span>
              <span className={`chip ${STATE_STYLE[st].pill}`}>{st === 'checking' ? 'Checking call…' : timePill(t.due_at)}</span>
            </button>
          );
        })}
      </div>
      <div className="flex gap-2 mt-3 flex-wrap">
        <button className="btn btn-sm" onClick={onSchedule}>＋ Schedule</button>
        <button className="btn btn-sm !bg-gold !border-gold text-charcoal" onClick={onBookVisit}>📍 Site visit</button>
      </div>
    </div>
  );
};

/** Mark done / reschedule / cancel an open task — the nurture verbs. */
export const TaskSheet: React.FC<{
  task: Task;
  onClose: () => void;
  onChanged: () => void;
  onOpenVisit: () => void;
}> = ({ task, onClose, onChanged, onOpenVisit }) => {
  const [waCall, setWaCall] = useState(false);
  const [whatHappened, setWhatHappened] = useState('');
  const init = istParts(task.due_at);
  const [date, setDate] = useState(init.date);
  const [time, setTime] = useState(init.time);
  const [cancelReason, setCancelReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const brief = taskBriefing(task);
  const fromCrm = task.source === 'selldo';

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true); setError(null);
    try { await fn(); onChanged(); onClose(); } catch (e) { setError(errText(e)); setBusy(false); }
  };

  const markDone = () => run(async () => {
    if (task.type === 'call' && waCall) {
      // Operator-attested WhatsApp call: the activity is the evidence, and it
      // becomes the task's result. Both legs are idempotent.
      const act = await activityApi.append({
        lead_id: task.lead_id, kind: 'call', channel: 'whatsapp', direction: 'outbound',
        source_ref: `operator:wa_call:${task.id}`, task_id: task.id,
        summary: `WhatsApp call: ${whatHappened.trim().slice(0, 140)}`,
        payload: { medium: 'whatsapp_call', via: 'operator_personal_wa', connection_result: 'connected', notes: whatHappened.trim(), task_id: task.id },
      });
      await actionApi.complete(task.id, undefined, act.id);
    } else {
      await actionApi.complete(task.id);
    }
  });

  return (
    <Sheet title={taskTitle(task)} sub="Action" onClose={onClose} error={error}
      footer={!fromCrm && (
        <button className="btn btn-pri" disabled={busy || (waCall && !whatHappened.trim())} onClick={markDone}>Mark done</button>
      )}>
      {brief && (
        <div className="bg-cream border-[1.5px] border-sketch rounded-lg p-3">
          <SheetLabel>Why this is scheduled</SheetLabel>
          <p className="text-[13px] italic m-0">“{brief}”</p>
        </div>
      )}
      {fromCrm ? (
        <p className="text-sm text-graphite">This follow-up is managed in Sell.do. Close or move it there.</p>
      ) : (
        <>
          {task.type === 'call' && (
            <div>
              <label className="flex items-center gap-2 text-[13px]">
                <input id="wa-call" type="checkbox" checked={waCall} onChange={(e) => setWaCall(e.target.checked)} />
                This was a WhatsApp call
              </label>
              {waCall && (
                <textarea id="wa-call-notes" className="inp w-full mt-2" rows={3} placeholder="What happened on the call?"
                  value={whatHappened} onChange={(e) => setWhatHappened(e.target.value)} />
              )}
            </div>
          )}
          <div>
            <SheetLabel>Reschedule</SheetLabel>
            {task.type === 'site_visit' ? (
              <button className="btn btn-sm" onClick={() => { onClose(); onOpenVisit(); }}>Move the visit…</button>
            ) : (
              <div className="flex gap-2 flex-wrap items-center">
                <input id="rs-date" type="date" className="inp" value={date} onChange={(e) => setDate(e.target.value)} />
                <input id="rs-time" type="time" className="inp" value={time} onChange={(e) => setTime(e.target.value)} />
                <button className="btn btn-sm" disabled={busy || !date || !time}
                  onClick={() => run(() => actionApi.reschedule(task.id, istInstant(date, time)))}>Reschedule</button>
              </div>
            )}
          </div>
          <div>
            <SheetLabel>Cancel · reason required</SheetLabel>
            <div className="flex gap-2">
              <input id="cancel-reason" className="inp flex-1" placeholder="Why cancel?" value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)} />
              <button className="btn btn-sm !text-brick !border-brick/40" disabled={busy || !cancelReason.trim()}
                onClick={() => run(() => actionApi.cancel(task.id, cancelReason.trim()))}>Cancel action</button>
            </div>
          </div>
        </>
      )}
    </Sheet>
  );
};

/** Schedule a call / WhatsApp template / to-do — or, given `proposal`, review
 *  an AI-suggested one (type fixed, fields pre-filled, approve or reject). */
export const ScheduleSheet: React.FC<{
  lead: Lead;
  proposal?: Task | null;
  onClose: () => void;
  onChanged: () => void;
}> = ({ lead, proposal, onClose, onChanged }) => {
  const init = proposal ? istParts(proposal.due_at) : { date: shiftDay(istToday(), 1), time: '10:00' };
  const [type, setType] = useState<'call' | 'whatsapp' | 'other'>((proposal?.type as any) || 'call');
  const [date, setDate] = useState(init.date);
  const [time, setTime] = useState(init.time);
  const [note, setNote] = useState<string>(proposal ? (proposal.payload?.title || '').replace(/\s*\(AI suggested\)\s*/i, '') : '');
  const [templates, setTemplates] = useState<WhatsAppTemplate[] | null>(null);
  const [templateId, setTemplateId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (type !== 'whatsapp' || templates) return;
    marketingApi.getTemplates('OUTBOUND', 'APPROVED').then((ts) => {
      setTemplates(ts);
      const hinted = ts.find((t) => t.name === proposal?.payload?.template_name);
      if (hinted) setTemplateId(hinted.id);
    }).catch((e) => setError(errText(e)));
  }, [type, templates, proposal]);

  const template = templates?.find((t) => t.id === templateId);
  const canSave = !busy && date && time && (type !== 'whatsapp' || template) && (type !== 'other' || note.trim());

  const save = async () => {
    setBusy(true); setError(null);
    const dueAt = istInstant(date, time);
    try {
      if (proposal) {
        const data = type === 'whatsapp'
          ? { title: template!.display_name || template!.name, template_id: template!.id }
          : note.trim() ? { title: note.trim() } : {};
        await actionApi.approve(proposal.id, dueAt, data);
      } else if (type === 'other') {
        await taskApi.openTodo(lead.id, dueAt, note.trim());
      } else if (type === 'whatsapp') {
        await actionApi.schedule({ lead_id: lead.id, type, scheduled_at: dueAt,
          title: template!.display_name || template!.name, data: { template_id: template!.id } });
      } else {
        await actionApi.schedule({ lead_id: lead.id, type, scheduled_at: dueAt, title: note.trim() || undefined });
      }
      onChanged(); onClose();
    } catch (e) { setError(errText(e)); setBusy(false); }
  };

  const reject = async () => {
    setBusy(true); setError(null);
    try { await taskApi.reject(proposal!.id); onChanged(); onClose(); } catch (e) { setError(errText(e)); setBusy(false); }
  };

  return (
    <Sheet title={proposal ? 'Review suggestion' : 'Schedule'} sub={lead.name} onClose={onClose} error={error}
      footer={<>
        {proposal ? <button className="btn" disabled={busy} onClick={reject}>✕ Reject</button>
          : <button className="btn" onClick={onClose}>Cancel</button>}
        <button className="btn btn-pri" disabled={!canSave} onClick={save}>{proposal ? 'Approve' : 'Schedule'}</button>
      </>}>
      {proposal?.payload?.suggestion?.reason && (
        <div className="bg-gold/10 border-[1.5px] border-gold/40 rounded-lg p-3">
          <span className="chip chip-gold">✦ AI</span>
          <p className="text-[13px] italic m-0 mt-1.5">“{proposal.payload.suggestion.reason}”</p>
        </div>
      )}
      <div>
        <SheetLabel>Type</SheetLabel>
        <div className="flex gap-1.5 flex-wrap">
          {([['call', 'Call'], ['whatsapp', 'WhatsApp'], ['other', 'To-do']] as const).map(([k, lbl]) => (
            <button key={k} type="button" className="pill" aria-pressed={type === k} disabled={!!proposal}
              onClick={() => setType(k)}>{lbl}</button>
          ))}
        </div>
      </div>
      <div>
        <SheetLabel>When (IST)</SheetLabel>
        <div className="flex gap-2 flex-wrap">
          <input id="sch-date" type="date" className="inp" value={date} onChange={(e) => setDate(e.target.value)} />
          <input id="sch-time" type="time" className="inp" value={time} onChange={(e) => setTime(e.target.value)} />
        </div>
      </div>
      {type === 'whatsapp' && (
        <div>
          <SheetLabel>Template</SheetLabel>
          {!templates ? <p className="text-sm text-graphite m-0">Loading templates…</p>
            : templates.length === 0 ? <p className="text-sm text-graphite m-0">No approved templates available</p>
              : (
                <select id="sch-template" className="inp w-full" value={templateId} onChange={(e) => setTemplateId(e.target.value)}>
                  <option value="">Pick a template</option>
                  {templates.map((t) => <option key={t.id} value={t.id}>{t.display_name || t.name}</option>)}
                </select>
              )}
          {template?.body && <p className="text-[12px] text-graphite whitespace-pre-wrap mt-2 m-0 line-clamp-6">{template.body}</p>}
        </div>
      )}
      {type !== 'whatsapp' && (
        <div>
          <SheetLabel>{type === 'other' ? 'What to do' : 'Note (optional)'}</SheetLabel>
          <textarea id="sch-note" className="inp w-full" rows={3} value={note} onChange={(e) => setNote(e.target.value)}
            placeholder={type === 'other' ? 'e.g. Send the price sheet' : 'Why this call?'} />
        </div>
      )}
    </Sheet>
  );
};

// ── Engagement + album ──────────────────────────────────────────────────────

const FORM_LABEL: Record<string, string> = {
  project_album: 'Project Album', welcome_video: 'Welcome Video', welcome_project: 'Project Page',
  villa_style: 'Villa Style Form', site_visit: 'Site Visit Form', watch_construction_update: 'Construction Update',
  watch_construction_update_latest: 'Construction Update', testimonial: 'Testimonial', award: 'Award Video',
  website: 'Website visitor',
};

// Clarity project for bhuvanafortunegreens.com. Sessions are identified by the
// visitor token, so a lead's recordings are one filter away: Recordings →
// Custom filters → User ID = token. Clarity documents no URL parameter for that
// filter, hence copy-then-open.
const CLARITY_RECORDINGS_URL = 'https://clarity.microsoft.com/projects/view/yf84xmgws2/impressions';
const CLARITY_FORM_TYPES = new Set(['website', 'project_album']);

export const EngagementCard: React.FC<{ engagement: LeadEngagement | null }> = ({ engagement }) => {
  const links = engagement?.links.filter(isEngaged) || [];
  if (!links.length) return null;
  return (
    <FoldCard title="Link engagement" icon={<MousePointerClick className="w-4 h-4" strokeWidth={1.75} />}
      count={`${links.length} link${links.length === 1 ? '' : 's'}`}>
      <div className="space-y-2.5">
        {links.map((l) => (
          <div key={l.link_id} className="text-[12.5px]">
            <b className="text-[13px]">{FORM_LABEL[l.form_type] || l.form_type}</b>
            <div className="text-graphite">
              {[
                l.open_count ? `opened ${l.open_count}×` : null,
                l.distinct_frames > 0 ? `${l.distinct_frames} frames` : null,
                fmtDwell(l.dwell_ms_total),
                l.liked_frames?.length ? `♥ ${l.liked_frames.join(', ')}` : null,
                l.shared_frames?.length ? `shared ${l.shared_frames.join(', ')}` : null,
                l.brochure_opens > 0 ? 'brochure ✓' : null,
                l.button_clicked_at ? `CTA: ${(l.button_intent || 'clicked').replace(/_/g, ' ')}` : null,
                l.video_pct_watched ? `${l.video_pct_watched}% watched` : null,
                l.submitted_at ? 'submitted ✓' : null,
              ].filter(Boolean).join(' · ')}
            </div>
            {l.source_labels.map((label, i) => (
              <div key={l.sources[i] ?? label} className="text-graphite">via {label}</div>
            ))}
            <div className="text-graphite flex items-center gap-2">
              <span className="num">{l.token}</span>
              <button type="button" className="text-forest hover:underline" title="Copy visitor token"
                onClick={() => navigator.clipboard.writeText(l.token)}>copy</button>
              {CLARITY_FORM_TYPES.has(l.form_type) && (
                <a href={CLARITY_RECORDINGS_URL} target="_blank" rel="noreferrer" className="text-forest hover:underline"
                  title="Opens Clarity with the token copied — paste it into Custom filters → User ID"
                  onClick={() => navigator.clipboard.writeText(l.token)}>Recordings ↗</a>
              )}
            </div>
          </div>
        ))}
      </div>
    </FoldCard>
  );
};

export const AlbumCard: React.FC<{ engagement: LeadEngagement | null }> = ({ engagement }) => {
  const links = engagement?.links.filter((l) => l.form_type === 'project_album' && isEngaged(l)) || [];
  if (!links.length) return null;
  const liked = new Set(links.flatMap((l) => l.liked_frames || []));
  const shared = new Set(links.flatMap((l) => l.shared_frames || []));
  const byFrame = new Map<string, { idx: number | null; views: number; dwell: number }>();
  links.flatMap((l) => l.frame_stats || []).forEach((f) => {
    const r = byFrame.get(f.frame) || { idx: f.idx, views: 0, dwell: 0 };
    r.idx = r.idx ?? f.idx; r.views += f.views; r.dwell += f.dwell_ms;
    byFrame.set(f.frame, r);
  });
  // Album order; frames that have since left the album trail.
  const frames = Array.from(byFrame.entries())
    .sort(([, a], [, b]) => (a.idx ?? Infinity) - (b.idx ?? Infinity));
  const maxDwell = Math.max(1, ...frames.map(([, f]) => f.dwell));
  const opens = links.reduce((n, l) => n + (l.open_count || 0), 0);
  const cta = links.find((l) => l.button_clicked_at);
  const summary = [
    `${frames.length} frame${frames.length === 1 ? '' : 's'} seen`,
    fmtDwell(links.reduce((n, l) => n + l.dwell_ms_total, 0)),
    liked.size ? `♥ ${liked.size}` : null,
    shared.size ? `shared ${shared.size}` : null,
    links.some((l) => l.brochure_opens > 0) ? 'brochure ✓' : null,
    cta ? `CTA: ${(cta.button_intent || 'clicked').replace(/_/g, ' ')}` : null,
  ].filter(Boolean).join(' · ');
  return (
    <FoldCard title="Album" icon={<Image className="w-4 h-4" strokeWidth={1.75} />}
      count={`${opens} open${opens === 1 ? '' : 's'}`} defaultOpen={false}>
      <p className="text-[12.5px] font-semibold m-0 mb-2">{summary}</p>
      <div className="space-y-1">
        {frames.map(([code, f]) => (
          <div key={code} className="grid grid-cols-[52px_minmax(0,1fr)_auto] gap-2 items-center text-[12px]">
            <span className="font-semibold num">{code}</span>
            <span className="h-1.5 rounded bg-forest/10 overflow-hidden"><i className="block h-full bg-forest" style={{ width: `${(f.dwell / maxDwell) * 100}%` }} /></span>
            <span className="num text-graphite">
              {f.views} view{f.views === 1 ? '' : 's'}{fmtDwell(f.dwell) ? ` · ${fmtDwell(f.dwell)}` : ''}
              {liked.has(code) && <span className="text-brick ml-1">♥</span>}
              {shared.has(code) && <span className="text-forest ml-1">↗</span>}
            </span>
          </div>
        ))}
      </div>
    </FoldCard>
  );
};

// ── Note (text or voice) ────────────────────────────────────────────────────

export const NoteCard: React.FC<{ lead: Lead; onSaved: () => void }> = ({ lead, onSaved }) => {
  const [text, setText] = useState('');
  const [audio, setAudio] = useState<Blob | null>(null);
  const [recording, setRecording] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const rec = useRef<MediaRecorder | null>(null);
  const audioUrl = useMemo(() => (audio ? URL.createObjectURL(audio) : null), [audio]);
  useEffect(() => () => { if (audioUrl) URL.revokeObjectURL(audioUrl); }, [audioUrl]);

  const startRec = async () => {
    setError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const chunks: Blob[] = [];
      const r = new MediaRecorder(stream);
      r.ondataavailable = (e) => chunks.push(e.data);
      r.onstop = () => { stream.getTracks().forEach((t) => t.stop()); setAudio(new Blob(chunks, { type: r.mimeType })); };
      r.start(); rec.current = r; setRecording(true);
    } catch { setError('Microphone access was refused. Allow it in the browser to record.'); }
  };
  const stopRec = () => { rec.current?.stop(); setRecording(false); };

  const save = async () => {
    setBusy(true); setError(null);
    try {
      if (audio) await leadApi.addVoiceNote(lead.id, audio, crypto.randomUUID(), text.trim() || undefined);
      else await leadApi.addNote(lead.id, text.trim());
      setText(''); setAudio(null); onSaved();
    } catch (e) { setError(errText(e)); }
    setBusy(false);
  };

  return (
    <div className="card">
      <h3 className="card-h"><StickyNote className="w-4 h-4" strokeWidth={1.75} />Add note</h3>
      <textarea id="note-text" className="inp w-full" rows={3} placeholder="What happened?" value={text}
        onChange={(e) => setText(e.target.value)} />
      {audioUrl && (
        <div className="flex items-center gap-2 mt-2">
          <audio controls className="h-8 flex-1 min-w-0" src={audioUrl} />
          <button className="btn btn-sm" onClick={() => setAudio(null)}>✕</button>
        </div>
      )}
      <div className="flex gap-2 mt-2">
        {recording ? (
          <button className="btn btn-sm !text-brick !border-brick/40" onClick={stopRec}><Square className="w-3 h-3 inline mr-1" />Stop</button>
        ) : (
          <button className="btn btn-sm" disabled={busy} onClick={startRec}><Mic className="w-3.5 h-3.5 inline mr-1" strokeWidth={1.9} />{audio ? 'Record again' : 'Voice note'}</button>
        )}
        <button className="btn btn-sm btn-pri ml-auto" disabled={busy || recording || (!audio && !text.trim())} onClick={save}>
          {busy ? 'Saving…' : 'Save note'}
        </button>
      </div>
      {error && <p className="text-brick text-xs mt-2 m-0">{error}</p>}
    </div>
  );
};
